import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Tables } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { STORAGE_BUCKETS } from "./storage";
import { queryKeys } from "./keys";

// =============================================================================
// Messagerie — conversations + messages + participants
//
// Schéma : `conversations` × `conversation_participants` × `messages`
// Deux canaux (enum `message_channel`) :
//   - salarie_production : salarié ↔ équipe production Aon (consultant + chef projet)
//   - drh_chefprojet     : DRH ↔ chef de projet Aon
//
// Le trigger `app.touch_conversation_on_message` met automatiquement à jour
// `last_message_at` à chaque INSERT message.
// =============================================================================

export type MessageChannel = "salarie_production" | "drh_chefprojet";
export type Conversation = Tables<"conversations">;
export type ConversationParticipant = Tables<"conversation_participants">;
export type Message = Tables<"messages">;
export type MessageAttachment = Tables<"message_attachments">;

// -----------------------------------------------------------------------------
// Types enrichis
// -----------------------------------------------------------------------------
export interface ParticipantWithProfile extends ConversationParticipant {
  profile: Pick<Tables<"profiles">, "user_id" | "prenom" | "nom" | "email" | "avatar_url"> | null;
}

export interface ConversationSummary extends Conversation {
  participants: ParticipantWithProfile[];
  last_message: Message | null;
  unread_count: number;
}

export interface MessageWithSender extends Message {
  sender: Pick<Tables<"profiles">, "user_id" | "prenom" | "nom" | "email" | "avatar_url"> | null;
  attachments: MessageAttachment[];
}

// =============================================================================
// LECTURE
// =============================================================================

/**
 * Liste les conversations auxquelles l'utilisateur connecté participe.
 *
 * Stratégie : on récupère d'abord les `conversation_participants` du user
 * (avec son `last_read_at`), puis on hydrate les conversations + dernier
 * message + count des non-lus.
 *
 * Tri : `last_message_at` desc (les threads "vivants" en premier).
 */
export function useMyConversations(filters?: {
  channel?: MessageChannel;
  includeArchived?: boolean;
}) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [
      ...queryKeys.messaging.conversations(user?.id ?? "anon"),
      filters?.channel ?? "all",
      filters?.includeArchived ?? false,
    ] as const,
    enabled: Boolean(user?.id),
    queryFn: async (): Promise<ConversationSummary[]> => {
      if (!user) return [];

      // 1. Mes participations (= mes threads + mon last_read_at)
      const { data: myParts, error: pErr } = await supabase
        .from("conversation_participants")
        .select("conversation_id, last_read_at")
        .eq("user_id", user.id);
      if (pErr) throw pErr;
      const convIds = (myParts ?? []).map((p) => p.conversation_id);
      if (convIds.length === 0) return [];

      const lastReadMap = new Map((myParts ?? []).map((p) => [p.conversation_id, p.last_read_at]));

      // 2. Conversations + leurs participants
      let q = supabase
        .from("conversations")
        .select(
          `
          *,
          participants:conversation_participants (
            conversation_id, user_id, role_in_thread, joined_at, last_read_at, is_muted,
            profile:profiles!conversation_participants_user_id_fkey (
              user_id, prenom, nom, email, avatar_url
            )
          )
        `,
        )
        .in("id", convIds)
        .order("last_message_at", { ascending: false, nullsFirst: false });

      if (filters?.channel) q = q.eq("channel", filters.channel);
      if (!filters?.includeArchived) q = q.eq("is_archived", false);

      const { data: convs, error: cErr } = await q;
      if (cErr) throw cErr;
      if (!convs || convs.length === 0) return [];

      // 3. Pour chaque conversation : dernier message + count non-lus
      const summaries: ConversationSummary[] = await Promise.all(
        convs.map(async (c) => {
          const myLastRead = lastReadMap.get(c.id) ?? null;

          // Dernier message
          const { data: lastMsg } = await supabase
            .from("messages")
            .select("*")
            .eq("conversation_id", c.id)
            .is("deleted_at", null)
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();

          // Count non-lus : messages > last_read_at && sender != moi
          let unread = 0;
          if (myLastRead) {
            const { count } = await supabase
              .from("messages")
              .select("id", { count: "exact", head: true })
              .eq("conversation_id", c.id)
              .is("deleted_at", null)
              .gt("created_at", myLastRead)
              .neq("sender_id", user.id);
            unread = count ?? 0;
          } else {
            // Jamais lu : tout est non-lu (hors messages que j'ai envoyés)
            const { count } = await supabase
              .from("messages")
              .select("id", { count: "exact", head: true })
              .eq("conversation_id", c.id)
              .is("deleted_at", null)
              .neq("sender_id", user.id);
            unread = count ?? 0;
          }

          return {
            ...(c as Conversation),
            participants:
              (c as unknown as { participants: ParticipantWithProfile[] }).participants ?? [],
            last_message: (lastMsg as Message | null) ?? null,
            unread_count: unread,
          };
        }),
      );

      return summaries;
    },
    // Fallback : refetch toutes les 60s si Realtime tombe.
    refetchInterval: 60_000,
  });
}

/**
 * Charge les messages d'une conversation avec sender et attachments.
 * Realtime via Supabase pour les nouveaux messages + fallback polling 60s.
 */
export function useConversationMessages(conversationId: string | undefined) {
  return useQuery({
    queryKey: conversationId
      ? queryKeys.messaging.messages(conversationId)
      : (["messaging", "messages", "none"] as const),
    enabled: Boolean(conversationId),
    refetchInterval: 60_000,
    queryFn: async (): Promise<MessageWithSender[]> => {
      if (!conversationId) return [];
      const { data, error } = await supabase
        .from("messages")
        .select(
          `
          *,
          sender:profiles!messages_sender_id_fkey (
            user_id, prenom, nom, email, avatar_url
          ),
          attachments:message_attachments (*)
        `,
        )
        .eq("conversation_id", conversationId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as MessageWithSender[];
    },
  });
}

/**
 * Compteur global des messages non-lus pour l'utilisateur connecté.
 *
 * Utilise la RPC SQL `app.unread_messages_count` (1 seule requête agrégée)
 * pour la perf. Fallback : si la RPC n'existe pas (migration non poussée),
 * on retombe sur la stratégie N+1 client-side.
 */
export function useUnreadMessagesCount() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["messaging", "unread-count", user?.id ?? "anon"] as const,
    enabled: Boolean(user?.id),
    // Fallback : refetch toutes les 60s ; Realtime poussera les MAJ entre-temps
    refetchInterval: 60_000,
    queryFn: async (): Promise<number> => {
      if (!user) return 0;

      // Tente la RPC en priorité. Appel direct sur le client pour conserver le
      // binding `this` (sinon supabase-js lève « Cannot read properties of
      // undefined (reading 'rest') »).
      const rpcClient = supabase as unknown as {
        rpc: (
          name: string,
          params?: Record<string, unknown>,
        ) => Promise<{ data: number | null; error: { message: string } | null }>;
      };

      const { data, error } = await rpcClient.rpc("unread_messages_count", {
        p_user_id: user.id,
      });
      if (!error && typeof data === "number") {
        return data;
      }

      // Fallback N+1 client-side (migration RPC non encore appliquée)
      const { data: myParts } = await supabase
        .from("conversation_participants")
        .select("conversation_id, last_read_at")
        .eq("user_id", user.id);
      if (!myParts || myParts.length === 0) return 0;

      let total = 0;
      for (const p of myParts) {
        let q = supabase
          .from("messages")
          .select("id", { count: "exact", head: true })
          .eq("conversation_id", p.conversation_id)
          .is("deleted_at", null)
          .neq("sender_id", user.id);
        if (p.last_read_at) q = q.gt("created_at", p.last_read_at);
        const { count } = await q;
        total += count ?? 0;
      }
      return total;
    },
  });
}

// =============================================================================
// REALTIME — invalidation des caches sur événements Postgres
// =============================================================================

/**
 * Hook : s'abonne aux events Realtime pour invalider automatiquement les
 * caches messagerie de l'utilisateur courant.
 *
 * À monter une seule fois (typiquement dans `AppLayout`) ; gère :
 *   - INSERT/UPDATE messages → invalide messages + conversations + unread
 *   - UPDATE conversations → invalide la liste (bump last_message_at)
 *
 * Si la connexion Realtime tombe, le polling (refetchInterval) prend le relais.
 */
export function useMessagingRealtime() {
  const { user } = useAuth();
  const qc = useQueryClient();

  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`messaging:${user.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "messages" }, () => {
        void qc.invalidateQueries({ queryKey: ["messaging"] });
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "conversations" }, () => {
        void qc.invalidateQueries({ queryKey: ["messaging", "conversations"] });
      })
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "message_attachments" },
        () => {
          void qc.invalidateQueries({ queryKey: ["messaging", "messages"] });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user?.id, qc]);
}

// =============================================================================
// MUTATIONS
// =============================================================================

export interface SendMessageInput {
  conversationId: string;
  body: string;
  in_reply_to?: string | null;
  /** Fichiers à attacher au message (upload best-effort). */
  files?: File[];
}

/**
 * Envoie un message dans une conversation, avec PJ optionnelles.
 *
 * Flow :
 *   1. Insert du message (trigger DB bump last_message_at)
 *   2. Pour chaque fichier : upload Storage + insert message_attachments
 *
 * Si l'upload d'une PJ échoue, le message reste créé (best-effort) et l'erreur
 * est remontée pour que l'UI puisse l'afficher.
 */
export function useSendMessage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SendMessageInput) => {
      if (!user) throw new Error("Non authentifié");
      const { data, error } = await supabase
        .from("messages")
        .insert({
          conversation_id: input.conversationId,
          sender_id: user.id,
          body: input.body.trim(),
          in_reply_to: input.in_reply_to ?? null,
        })
        .select(
          `
          *,
          sender:profiles!messages_sender_id_fkey (
            user_id, prenom, nom, email, avatar_url
          )
        `,
        )
        .single();
      if (error) throw error;
      const msg = data as unknown as MessageWithSender;

      // Upload PJ en parallèle si fournies
      if (input.files && input.files.length > 0) {
        await Promise.all(
          input.files.map((file) =>
            uploadMessageAttachment({
              conversationId: input.conversationId,
              messageId: msg.id,
              file,
            }),
          ),
        );
      }

      return msg;
    },
    onSuccess: (msg, vars) => {
      void qc.invalidateQueries({
        queryKey: queryKeys.messaging.messages(vars.conversationId),
      });
      void qc.invalidateQueries({ queryKey: ["messaging", "conversations"] });
      void qc.invalidateQueries({ queryKey: ["messaging", "unread-count"] });

      // Notifie les autres participants par email (best-effort, silencieux si
      // échec). L'edge function `message-notify` tourne en service role et
      // envoie dans LES DEUX SENS (salarié → Aon comme Aon → salarié).
      void notifyParticipantsOfNewMessage(msg.id);
    },
  });
}

// -----------------------------------------------------------------------------
// Helper : notification email aux autres participants
//
// Délègue à l'edge function `message-notify` (service role) qui envoie les
// emails dans les DEUX sens (salarié ↔ Aon) — l'ancienne approche client-side
// échouait silencieusement quand l'expéditeur était un salarié (la fonction
// `send-email` est réservée aux internes Aon). Best-effort : silencieux si KO.
// -----------------------------------------------------------------------------
async function notifyParticipantsOfNewMessage(messageId: string): Promise<void> {
  try {
    await supabase.functions.invoke("message-notify", { body: { messageId } });
  } catch {
    // best-effort : on n'interrompt jamais l'UX d'envoi pour un email raté
  }
}

// =============================================================================
// PJ — Upload + URL signée
// =============================================================================

/**
 * Upload un fichier dans le bucket `message-attachments` et insère la ligne
 * `message_attachments` correspondante.
 *
 * Convention de chemin Storage : `{conversation_id}/{message_id}/{stamp}-{filename}`
 * Cette convention est utilisée par les policies RLS Storage.
 */
export async function uploadMessageAttachment(args: {
  conversationId: string;
  messageId: string;
  file: File;
}): Promise<Tables<"message_attachments">> {
  const stamp = Date.now().toString(36);
  const safeName = args.file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `${args.conversationId}/${args.messageId}/${stamp}-${safeName}`;

  const { error: upErr } = await supabase.storage
    .from(STORAGE_BUCKETS.messageAttachments)
    .upload(path, args.file, {
      cacheControl: "3600",
      contentType: args.file.type || undefined,
      upsert: false,
    });
  if (upErr) throw upErr;

  const { data, error } = await supabase
    .from("message_attachments")
    .insert({
      message_id: args.messageId,
      storage_path: path,
      filename: args.file.name,
      mime_type: args.file.type || null,
      size_bytes: args.file.size,
    })
    .select("*")
    .single();
  if (error) throw error;
  return data as Tables<"message_attachments">;
}

/**
 * Génère une URL signée pour télécharger une PJ (expiration 1h).
 * Le bucket `message-attachments` est privé, on doit donc signer chaque téléchargement.
 */
export async function getMessageAttachmentUrl(
  storagePath: string,
  expiresInSeconds = 3600,
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(STORAGE_BUCKETS.messageAttachments)
    .createSignedUrl(storagePath, expiresInSeconds);
  if (error) throw error;
  return data.signedUrl;
}

/**
 * Soft-delete un message dont je suis l'auteur (set `deleted_at = now()`).
 * Le contenu est masqué côté UI mais l'audit reste préservé.
 */
export function useDeleteMessage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { messageId: string; conversationId: string }) => {
      if (!user) throw new Error("Non authentifié");
      const { error } = await supabase
        .from("messages")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", args.messageId)
        .eq("sender_id", user.id); // RLS l'impose, mais filtre côté client aussi
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({
        queryKey: queryKeys.messaging.messages(vars.conversationId),
      });
      void qc.invalidateQueries({ queryKey: ["messaging", "conversations"] });
    },
  });
}

/** Marque la conversation comme lue (bump last_read_at à maintenant). */
export function useMarkConversationAsRead() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string) => {
      if (!user) return;
      const { error } = await supabase
        .from("conversation_participants")
        .update({ last_read_at: new Date().toISOString() })
        .eq("conversation_id", conversationId)
        .eq("user_id", user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["messaging", "conversations"] });
      void qc.invalidateQueries({ queryKey: ["messaging", "unread-count"] });
    },
  });
}

/**
 * Remise en NON LU (retour 31/07 — B8).
 *
 * L'état de lecture est porté par `conversation_participants.last_read_at` :
 * un message est lu par un destinataire dès que son last_read_at est ≥ au
 * created_at du message. Marquer un message comme non lu revient donc à
 * reculer SON last_read_at juste avant ce message : le message (et ceux qui
 * le suivent) redeviennent non lus dans les compteurs et les trois espaces.
 */
export function useMarkMessageUnread() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { conversationId: string; messageCreatedAt: string }) => {
      if (!user) return;
      const before = new Date(new Date(args.messageCreatedAt).getTime() - 1).toISOString();
      const { error } = await supabase
        .from("conversation_participants")
        .update({ last_read_at: before })
        .eq("conversation_id", args.conversationId)
        .eq("user_id", user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["messaging", "conversations"] });
      void qc.invalidateQueries({ queryKey: ["messaging", "unread-count"] });
    },
  });
}

/** Remet toute la conversation en non lu (dernier message compris). */
export function useMarkConversationUnread() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string) => {
      if (!user) return;
      // Dernier message reçu (pas de remise en non-lu sur ses propres messages).
      const { data: lastMsg, error: msgErr } = await supabase
        .from("messages")
        .select("created_at")
        .eq("conversation_id", conversationId)
        .is("deleted_at", null)
        .neq("sender_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (msgErr) throw msgErr;
      if (!lastMsg) return;
      const before = new Date(new Date(lastMsg.created_at).getTime() - 1).toISOString();
      const { error } = await supabase
        .from("conversation_participants")
        .update({ last_read_at: before })
        .eq("conversation_id", conversationId)
        .eq("user_id", user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["messaging", "conversations"] });
      void qc.invalidateQueries({ queryKey: ["messaging", "unread-count"] });
    },
  });
}

/** Archive ou désarchive une conversation. */
export function useToggleArchiveConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { conversationId: string; archive: boolean }) => {
      const { error } = await supabase
        .from("conversations")
        .update({ is_archived: args.archive })
        .eq("id", args.conversationId);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["messaging", "conversations"] });
    },
  });
}

// =============================================================================
// CRÉATION / ENSURE
// =============================================================================

/**
 * Récupère (ou crée) le thread `salarie_production` lié à un dossier.
 *
 * Idempotent : si un thread existe déjà pour ce dossier sur ce canal,
 * il est retourné tel quel. Sinon création + participants ajoutés.
 *
 * Participants ajoutés :
 *   - le salarié titulaire (via dossier.salarie_id → user_id)
 *   - le consultant assigné si présent
 *   - le chef de projet si présent
 */
/**
 * Récupère (ou crée) le thread `drh_chefprojet` lié à une opération.
 *
 * Participants ajoutés :
 *   - le chef de projet Aon de l'opération (`chef_projet_user_id`)
 *   - le sponsor DRH de l'opération (`sponsor_drh_user_id`)
 *
 * Si l'un des deux est null, il sera ajouté plus tard (par exemple quand
 * la DRH se connectera pour la 1re fois et appellera ce même hook).
 */
export function useEnsureOperationConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { operationId: string; subject?: string }): Promise<Conversation> => {
      // La création conversation + participants est faite atomiquement côté
      // serveur par la RPC `ensure_operation_conversation` (SECURITY DEFINER) :
      //  - contourne le verrou RLS poule/œuf (conv_insert exige déjà un
      //    participant ; conv_part_insert n'autorise que le staff Aon, donc un
      //    DRH ne pourrait ni créer la conversation ni s'y ajouter côté client) ;
      //  - gardée par `app.has_access_to_operation` ;
      //  - ajoute CP + DRH sponsor + l'appelant comme participants.
      // Idempotent : renvoie le thread `drh_chefprojet` existant si présent.
      const rpcClient = supabase as unknown as {
        rpc: (
          name: string,
          params?: Record<string, unknown>,
        ) => Promise<{ data: string | null; error: { message: string } | null }>;
      };
      const { data: convId, error: rpcErr } = await rpcClient.rpc("ensure_operation_conversation", {
        p_operation_id: args.operationId,
        p_subject: args.subject ?? null,
      });
      if (rpcErr) throw new Error(rpcErr.message);
      if (!convId) throw new Error("Conversation introuvable");

      const { data: conv, error: cErr } = await supabase
        .from("conversations")
        .select("*")
        .eq("id", convId)
        .single();
      if (cErr) throw cErr;
      return conv as Conversation;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["messaging", "conversations"] });
    },
  });
}

export function useEnsureDossierConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { dossierId: string; subject?: string }): Promise<Conversation> => {
      // La création conversation + participants est faite atomiquement côté
      // serveur par la RPC `ensure_dossier_conversation` (SECURITY DEFINER) :
      //  - contourne le verrou RLS poule/œuf (conv_insert exige déjà un
      //    participant ; conv_part_insert exige un rôle staff) ;
      //  - ajoute l'appelant interne Aon comme participant pour qu'il voie le fil.
      // Idempotent : renvoie le thread existant si présent.
      const rpcClient = supabase as unknown as {
        rpc: (
          name: string,
          params?: Record<string, unknown>,
        ) => Promise<{ data: string | null; error: { message: string } | null }>;
      };
      const { data: convId, error: rpcErr } = await rpcClient.rpc("ensure_dossier_conversation", {
        p_dossier_id: args.dossierId,
        p_subject: args.subject ?? null,
      });
      if (rpcErr) throw new Error(rpcErr.message);
      if (!convId) throw new Error("Conversation introuvable");

      const { data: conv, error: cErr } = await supabase
        .from("conversations")
        .select("*")
        .eq("id", convId)
        .single();
      if (cErr) throw cErr;
      return conv as Conversation;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["messaging", "conversations"] });
    },
  });
}
